let audioContext = null;
let audioLimiter = null;
let enabled = false;
let audioUnlockPromise = null;
let audioGestureBridgeInstalled = false;


// Alfa 1.3 · louder slot audio pass.
// WebAudio gain can exceed HTMLAudioElement volume=1.0, but keep a soft cap
// to avoid harsh clipping on square/saw tones.
const SLOT_AUDIO_MASTER_GAIN = 2.15;
const SLOT_AUDIO_MAX_GAIN = 0.145;

function getAudioContext() {
  if (!audioContext) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioContext = new AudioContextClass();
  }
  return audioContext;
}

export function unlockAudio() {
  const ctx = getAudioContext();
  if (!ctx) return Promise.resolve(false);
  enabled = true;

  const prime = () => {
    try {
      const buffer = ctx.createBuffer(1, 1, ctx.sampleRate || 44100);
      const source = ctx.createBufferSource();
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.00001, ctx.currentTime);
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(ctx.destination);
      source.start(0);
      source.stop(ctx.currentTime + 0.01);
    } catch (_) {
      // Silent priming is best-effort only.
    }
    return true;
  };

  if (ctx.state === 'running') {
    prime();
    return Promise.resolve(true);
  }

  if (!audioUnlockPromise) {
    audioUnlockPromise = ctx.resume()
      .then(() => prime())
      .catch(() => false)
      .finally(() => {
        audioUnlockPromise = null;
      });
  }
  return audioUnlockPromise;
}

function runWhenAudioReady(callback) {
  const ctx = getAudioContext();
  if (!ctx || !enabled) return;

  const run = () => {
    if (ctx.state !== 'running') return;
    callback(ctx);
  };

  if (ctx.state === 'running') {
    run();
    return;
  }

  unlockAudio()?.then(run);
}

function installAudioGestureBridge() {
  if (audioGestureBridgeInstalled || typeof window === 'undefined' || typeof document === 'undefined') return;
  audioGestureBridgeInstalled = true;

  const unlockFromGesture = () => {
    unlockAudio();
  };

  ['pointerdown', 'mousedown', 'touchstart', 'keydown'].forEach((eventName) => {
    document.addEventListener(eventName, unlockFromGesture, { capture: true, passive: true });
  });
}

installAudioGestureBridge();


function getAudioOutputNode() {
  const ctx = getAudioContext();
  if (!ctx) return null;
  if (!audioLimiter) {
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-18, ctx.currentTime);
    compressor.knee.setValueAtTime(18, ctx.currentTime);
    compressor.ratio.setValueAtTime(8, ctx.currentTime);
    compressor.attack.setValueAtTime(0.004, ctx.currentTime);
    compressor.release.setValueAtTime(0.12, ctx.currentTime);
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.82, ctx.currentTime);
    compressor.connect(master);
    master.connect(ctx.destination);
    audioLimiter = compressor;
  }
  return audioLimiter;
}

function tone({ frequency = 440, duration = 0.08, type = 'square', gain = 0.04, when = 0, slideTo = null, boost = 1 }) {
  runWhenAudioReady((ctx) => {
    const now = ctx.currentTime + when;
    const oscillator = ctx.createOscillator();
    const amp = ctx.createGain();

    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, now);
    if (slideTo) oscillator.frequency.exponentialRampToValueAtTime(slideTo, now + duration);

    const peakGain = Math.min(gain * SLOT_AUDIO_MASTER_GAIN * boost, SLOT_AUDIO_MAX_GAIN);

    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(peakGain, now + 0.01);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(amp);
    amp.connect(getAudioOutputNode() || ctx.destination);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  });
}


function noiseBurst({ duration = 0.18, gain = 0.035, when = 0, filterFrequency = 2600, boost = 1 }) {
  runWhenAudioReady((ctx) => {
    const now = ctx.currentTime + when;
    const sampleCount = Math.max(1, Math.floor(ctx.sampleRate * duration));
    const buffer = ctx.createBuffer(1, sampleCount, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < sampleCount; i += 1) {
      const envelope = 1 - i / sampleCount;
      data[i] = (Math.random() * 2 - 1) * envelope;
    }

    const source = ctx.createBufferSource();
    const filter = ctx.createBiquadFilter();
    const amp = ctx.createGain();

    source.buffer = buffer;
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(filterFrequency, now);
    filter.Q.setValueAtTime(6.5, now);
    filter.frequency.exponentialRampToValueAtTime(Math.max(180, filterFrequency * 0.32), now + duration);

    const peakGain = Math.min(gain * SLOT_AUDIO_MASTER_GAIN * boost, SLOT_AUDIO_MAX_GAIN * 1.18);
    amp.gain.setValueAtTime(0.0001, now);
    amp.gain.exponentialRampToValueAtTime(peakGain, now + 0.012);
    amp.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter);
    filter.connect(amp);
    amp.connect(getAudioOutputNode() || ctx.destination);
    source.start(now);
    source.stop(now + duration + 0.02);
  });
}

export function playLever() {
  tone({ frequency: 180, slideTo: 75, duration: 0.22, type: 'sawtooth', gain: 0.035, boost: 1.25 });
}

export function playSpinLoopHint() {
  for (let i = 0; i < 7; i += 1) {
    tone({ frequency: 420 + i * 19, duration: 0.035, type: 'square', gain: 0.014, when: 0.08 + i * 0.052, boost: 1.45 });
  }
}


export function playTxCounterTick() {
  noiseBurst({ duration: 0.045, gain: 0.022, filterFrequency: 7600, boost: 1.35 });
  tone({ frequency: 1180, slideTo: 1680, duration: 0.055, type: 'square', gain: 0.020, when: 0.006, boost: 1.28 });
  tone({ frequency: 92, slideTo: 54, duration: 0.09, type: 'sawtooth', gain: 0.014, when: 0.035, boost: 1.12 });
}

export function playTxConfirmedNeutral() {
  tone({ frequency: 392, duration: 0.06, type: 'triangle', gain: 0.034, boost: 1.22 });
  tone({ frequency: 523, duration: 0.08, type: 'triangle', gain: 0.032, when: 0.075, boost: 1.22 });
}

export function playPrize() {
  [523, 659, 784, 1046].forEach((frequency, index) => {
    tone({ frequency, duration: 0.13, type: 'square', gain: 0.04, when: index * 0.085, boost: 1.55 });
  });
  tone({ frequency: 130, slideTo: 60, duration: 0.32, type: 'sawtooth', gain: 0.03, when: 0.18, boost: 1.45 });
}

export function playError() {
  tone({ frequency: 160, slideTo: 90, duration: 0.2, type: 'sawtooth', gain: 0.025, boost: 1.4 });
}

export function playConnectPrompt() {
  tone({ frequency: 740, duration: 0.055, type: 'square', gain: 0.032, boost: 1.25 });
  tone({ frequency: 988, duration: 0.065, type: 'square', gain: 0.03, when: 0.07, boost: 1.25 });
  tone({ frequency: 494, duration: 0.12, type: 'triangle', gain: 0.026, when: 0.16, boost: 1.2 });
}

export function playBrokenLever() {
  tone({ frequency: 95, slideTo: 48, duration: 0.18, type: 'sawtooth', gain: 0.045, boost: 1.6 });
  tone({ frequency: 310, slideTo: 120, duration: 0.08, type: 'square', gain: 0.028, when: 0.06, boost: 1.45 });
  tone({ frequency: 70, slideTo: 42, duration: 0.24, type: 'sawtooth', gain: 0.032, when: 0.16, boost: 1.5 });
}


export function playLeverFusePop() {
  // Beta 3.1.9: broken lever fuse burst. Short, bright and limiter-safe.
  noiseBurst({ duration: 0.052, gain: 0.010, filterFrequency: 7600, boost: 1.05 });
  tone({ frequency: 1180, slideTo: 420, duration: 0.075, type: 'triangle', gain: 0.014, when: 0.012, boost: 0.95 });
  tone({ frequency: 92, slideTo: 46, duration: 0.22, type: 'sine', gain: 0.018, when: 0.055, boost: 0.90 });
  noiseBurst({ duration: 0.040, gain: 0.006, filterFrequency: 4200, when: 0.125, boost: 0.82 });
}


export function playNeonLogoFault({ volume = 0.28, letters = 1 } = {}) {
  // Decorative broken-neon logo ping. It respects the existing audio unlock guard
  // through tone/noiseBurst and never runs before the browser allows audio.
  const safeVolume = Math.max(0, Math.min(1, Number(volume) || 0));
  if (safeVolume <= 0) return;

  const letterBoost = Math.max(0.72, Math.min(1.18, 0.74 + (Number(letters) || 1) * 0.12));
  const baseGain = 0.006 + safeVolume * 0.018;

  noiseBurst({ duration: 0.022, gain: baseGain * 0.45, filterFrequency: 7800, boost: 0.42 * letterBoost });
  tone({ frequency: 1480, slideTo: 920, duration: 0.045, type: 'sine', gain: baseGain, when: 0.010, boost: 0.36 * letterBoost });
  tone({ frequency: 2210, slideTo: 1320, duration: 0.034, type: 'triangle', gain: baseGain * 0.64, when: 0.054, boost: 0.32 * letterBoost });
  tone({ frequency: 310, slideTo: 172, duration: 0.090, type: 'sine', gain: baseGain * 0.42, when: 0.096, boost: 0.28 * letterBoost });
}


export function playMultiplierSelect(multiplier = 1) {
  // Beta 1.4.2 · right multiplier rail softened.
  // Keep each multiplier distinct, but remove harsh square/saw peaks that were
  // causing audible crunch on some speakers/headphones.
  const safe = Number(multiplier) || 1;
  const profile = {
    1: { a: 520, b: 740, n: 3000, boost: 0.58 },
    3: { a: 600, b: 900, n: 3400, boost: 0.64 },
    5: { a: 690, b: 1080, n: 3900, boost: 0.70 },
    10: { a: 790, b: 1320, n: 4600, boost: 0.76 },
    100: { a: 900, b: 1680, n: 5400, boost: 0.84 },
  }[safe] || { a: 620, b: 920, n: 3400, boost: 0.62 };

  noiseBurst({ duration: 0.022, gain: 0.0065, filterFrequency: profile.n, boost: profile.boost });
  tone({ frequency: profile.a, slideTo: profile.b, duration: 0.070, type: 'triangle', gain: 0.012, when: 0.006, boost: profile.boost });
  tone({ frequency: Math.max(120, profile.a / 3), duration: 0.050, type: 'sine', gain: 0.0075, when: 0.070, boost: profile.boost * 0.82 });

  if (safe >= 10) {
    noiseBurst({ duration: 0.030, gain: 0.0042, filterFrequency: profile.n * 0.62, when: 0.105, boost: profile.boost * 0.78 });
  }
}

export function playJackpotAward() {
  // Big but still synthesized: bell + alarm + electric burst for VANGUARD Jackpot reveal.
  noiseBurst({ duration: 0.16, gain: 0.042, filterFrequency: 8400, boost: 1.9 });
  [880, 1175, 1568, 2093].forEach((frequency, index) => {
    tone({ frequency, duration: 0.16, type: 'square', gain: 0.043, when: 0.02 + index * 0.10, boost: 1.85 });
    tone({ frequency: frequency / 2, duration: 0.20, type: 'triangle', gain: 0.024, when: 0.04 + index * 0.10, boost: 1.45 });
  });
  tone({ frequency: 132, slideTo: 58, duration: 0.62, type: 'sawtooth', gain: 0.040, when: 0.16, boost: 1.7 });
  noiseBurst({ duration: 0.20, gain: 0.030, filterFrequency: 5200, when: 0.46, boost: 1.55 });
}

export function playJackpotClaim() {
  tone({ frequency: 1046, duration: 0.12, type: 'square', gain: 0.040, boost: 1.65 });
  tone({ frequency: 1568, duration: 0.14, type: 'square', gain: 0.036, when: 0.10, boost: 1.65 });
  tone({ frequency: 2093, duration: 0.18, type: 'triangle', gain: 0.034, when: 0.22, boost: 1.55 });
  noiseBurst({ duration: 0.08, gain: 0.018, filterFrequency: 7400, when: 0.12, boost: 1.42 });
}


// Beta 1.3 · VANGUARD clean synth UI button language.
// Softer waveforms + limiter-safe gain: electric, but without harsh clipping/distortion.
export function playBuyButtonSynth() {
  // Bright ascending charge: BUY = energy in.
  noiseBurst({ duration: 0.024, gain: 0.0075, filterFrequency: 5200, boost: 0.82 });
  tone({ frequency: 440, slideTo: 880, duration: 0.115, type: 'triangle', gain: 0.020, when: 0.000, boost: 0.94 });
  tone({ frequency: 660, slideTo: 1320, duration: 0.095, type: 'sine', gain: 0.015, when: 0.050, boost: 0.90 });
  tone({ frequency: 1760, duration: 0.038, type: 'triangle', gain: 0.009, when: 0.135, boost: 0.82 });
}

export function playSellButtonSynth() {
  // Dark descending discharge: SELL = energy out, clean and controlled.
  noiseBurst({ duration: 0.028, gain: 0.0068, filterFrequency: 3200, boost: 0.82 });
  tone({ frequency: 660, slideTo: 260, duration: 0.130, type: 'triangle', gain: 0.019, when: 0.000, boost: 0.94 });
  tone({ frequency: 220, slideTo: 110, duration: 0.135, type: 'sine', gain: 0.013, when: 0.055, boost: 0.86 });
}

export function playSideButtonSynth(index = 0) {
  // Discreet sci-fi console blips; each left rail button gets its own accent.
  const profiles = [
    { a: 620, b: 930, n: 3800 },
    { a: 700, b: 520, n: 4300 },
    { a: 810, b: 1120, n: 5000 },
    { a: 560, b: 760, n: 3400 },
    { a: 920, b: 690, n: 5600 },
  ];
  const profile = profiles[Math.abs(Number(index) || 0) % profiles.length];
  noiseBurst({ duration: 0.016, gain: 0.0048, filterFrequency: profile.n, boost: 0.72 });
  tone({ frequency: profile.a, slideTo: profile.b, duration: 0.048, type: 'sine', gain: 0.0085, when: 0.004, boost: 0.76 });
}

export function playGeckoLaunchSynth() {
  // Aggressive terminal launch, but limited and rounded to avoid speaker crunch.
  noiseBurst({ duration: 0.060, gain: 0.017, filterFrequency: 7400, boost: 1.06 });
  tone({ frequency: 118, slideTo: 52, duration: 0.220, type: 'triangle', gain: 0.022, when: 0.000, boost: 1.06 });
  tone({ frequency: 1320, slideTo: 520, duration: 0.080, type: 'triangle', gain: 0.0145, when: 0.020, boost: 0.98 });
  noiseBurst({ duration: 0.052, gain: 0.010, filterFrequency: 3600, when: 0.095, boost: 0.94 });
  tone({ frequency: 72, duration: 0.095, type: 'sine', gain: 0.016, when: 0.145, boost: 0.96 });
}


export function playFooterButtonSynth(kind = 'default') {
  const profile = kind === 'address'
    ? { a: 540, b: 860, n: 4200 }
    : kind === 'wallet'
      ? { a: 620, b: 930, n: 4600 }
      : { a: 700, b: 1040, n: 5000 };

  noiseBurst({ duration: 0.018, gain: 0.0055, filterFrequency: profile.n, boost: 0.78 });
  tone({ frequency: profile.a, slideTo: profile.b, duration: 0.060, type: 'triangle', gain: 0.011, when: 0.004, boost: 0.86 });
  tone({ frequency: profile.b * 1.25, duration: 0.032, type: 'sine', gain: 0.006, when: 0.060, boost: 0.72 });
}

export function playPopupOpenSynth() {
  noiseBurst({ duration: 0.022, gain: 0.006, filterFrequency: 4600, boost: 0.72 });
  tone({ frequency: 520, slideTo: 780, duration: 0.070, type: 'triangle', gain: 0.010, when: 0.006, boost: 0.76 });
  tone({ frequency: 1040, duration: 0.050, type: 'sine', gain: 0.0065, when: 0.060, boost: 0.68 });
}

export function playPopupButtonSynth() {
  tone({ frequency: 760, slideTo: 1120, duration: 0.052, type: 'triangle', gain: 0.011, boost: 0.82 });
  noiseBurst({ duration: 0.014, gain: 0.0042, filterFrequency: 5400, when: 0.022, boost: 0.68 });
}

export function playStarCellReveal({ intensity = 1, column = 0 } = {}) {
  const boost = Math.min(1.55, 0.72 + intensity * 0.28);
  const offset = Math.max(0, Number(column) || 0) * 0.004;
  noiseBurst({ duration: 0.026, gain: 0.009, filterFrequency: 6200 + column * 360, when: offset, boost });
  tone({ frequency: 760 + column * 54, slideTo: 1320 + column * 68, duration: 0.070, type: 'triangle', gain: 0.015, when: offset + 0.006, boost });
  tone({ frequency: 190 + column * 12, slideTo: 92, duration: 0.092, type: 'sine', gain: 0.008, when: offset + 0.034, boost: boost * 0.74 });
}

export function playStarLineReveal({ intensity = 1, when = 0 } = {}) {
  const boost = Math.min(1.7, 0.82 + intensity * 0.32);
  noiseBurst({ duration: 0.050, gain: 0.012, filterFrequency: 7800, when, boost });
  tone({ frequency: 392, slideTo: 784, duration: 0.115, type: 'triangle', gain: 0.019, when: when + 0.010, boost });
  tone({ frequency: 98, slideTo: 56, duration: 0.180, type: 'sine', gain: 0.013, when: when + 0.055, boost: boost * 0.78 });
}

export function playOverdriveBurst() {
  noiseBurst({ duration: 0.055, gain: 0.011, filterFrequency: 8400, boost: 0.92 });
  tone({ frequency: 880, slideTo: 1760, duration: 0.090, type: 'triangle', gain: 0.012, when: 0.010, boost: 0.86 });
  tone({ frequency: 132, slideTo: 74, duration: 0.150, type: 'sine', gain: 0.010, when: 0.045, boost: 0.80 });
}

let lastLeverChargeUpdateAt = 0;

export function playLeverChargeStart() {
  noiseBurst({ duration: 0.020, gain: 0.0048, filterFrequency: 3600, boost: 0.62 });
  tone({ frequency: 180, slideTo: 260, duration: 0.075, type: 'triangle', gain: 0.010, when: 0.004, boost: 0.70 });
}

export function playLeverChargeUpdate(power = 0) {
  const nowMs = Date.now();
  if (nowMs - lastLeverChargeUpdateAt < 95) return;
  lastLeverChargeUpdateAt = nowMs;
  const p = Math.max(0, Math.min(1, Number(power) || 0));
  const boost = 0.48 + p * 0.34;
  tone({ frequency: 220 + p * 360, slideTo: 250 + p * 520, duration: 0.050, type: 'sine', gain: 0.0048 + p * 0.004, boost });
  if (p > 0.72) noiseBurst({ duration: 0.014, gain: 0.0028, filterFrequency: 5200 + p * 2200, boost: 0.46 + p * 0.22 });
}

export function playLeverReleaseBoost(power = 1) {
  const p = Math.max(0.25, Math.min(1, Number(power) || 1));
  const boost = 0.78 + p * 0.44;
  noiseBurst({ duration: 0.030 + p * 0.025, gain: 0.007 + p * 0.006, filterFrequency: 5200 + p * 2600, boost });
  tone({ frequency: 190 + p * 180, slideTo: 78 + p * 32, duration: 0.155 + p * 0.065, type: 'triangle', gain: 0.015 + p * 0.010, when: 0.000, boost });
  tone({ frequency: 520 + p * 620, slideTo: 900 + p * 1200, duration: 0.070 + p * 0.035, type: 'triangle', gain: 0.010 + p * 0.006, when: 0.025, boost: boost * 0.86 });
}
